import type { ChangeEvent, ReactNode } from 'react';
import { PROOF_MIME_TYPES } from '@bbbk/schema';
import { ButtonGlyph } from '../common/PendingButton';
import { Icon, DisclosureRegion } from '../common';
import { MessageSlot } from './MessageSlot';
import { ProofImage, isPdf, proofImageUrl } from './ProofImage';

// THE SELF-SERVICE CARD'S PARTS (G28) — what the Money card was built from,
// lifted out so the Vaccinations card is the same card and not a copy of it
// (Freddi, 2026-10-13: "the vaccination card should appear as a standalone
// card but should reuse all components from the deposit proof card").
//
// A band that is the head — the title, one sentence, the figure — with the
// message slot under it; a trail of rows, each a thumbnail, its words and its
// pill, that opens to one area — the shot with its ×, or a drop zone — and,
// under a shot waiting on Patricia, her decision. Nothing else beneath it
// (Freddi, 2026-10-13). Each card decides its own words and states; these parts
// only draw them, so the two cards cannot drift apart.

export type BandTone = 'none' | 'due' | 'warn' | 'ok';
export interface Band { tone: BandTone; sentence: string; figure: string; small: string }
export type PillTone = 'ok' | 'warn' | 'bad' | 'todo' | 'mute';
/** A row's pill: a word, or — with `glyph` — a mark and a date, as the customer's tags draw it. */
export interface Pill { tone: PillTone; word: string; glyph?: string }
export type TrailClass = 'seen' | 'read' | 'no' | 'mute';
/** The card's subject — its round mark at the head of the band (flow 4.7). */
export type CardSubject = 'money' | 'vax';

const SUBJECT_GLYPH: Record<CardSubject, string> = { money: 'euro', vax: 'syringe' };
function SubjectMark({ subject }: { subject: CardSubject }) {
  return <span className={`ss-ic ${subject}`}><Icon name={SUBJECT_GLYPH[subject]} size={14} /></span>;
}

/**
 * THE SPINE'S MARK (prototype flows 4.7, 5.1): one glyph for every row — the upload arrow — green when
 * something came in, grey when nothing has; the answer rides it as a corner badge: a tick for done or
 * valid, an eye for one waiting on Patricia, a cross for refused. The row's state decides it, so the
 * two cards cannot choose different marks for the same thing.
 */
const TICK: Record<TrailClass, { cls: string; glyph: string } | null> = {
  seen: { cls: 'us-ok', glyph: 'check' },
  read: { cls: 'us-eye', glyph: 'eye' },
  no: { cls: 'us-bad', glyph: 'close' },
  mute: null,
};
function SpineMark({ cls, uploaded }: { cls: TrailClass; uploaded: boolean }) {
  const tick = TICK[cls];
  return (
    <span className={`bflag ${uploaded ? 'tint-seen' : 'tint-mute'}`}>
      <Icon name="upload" size={10} />
      {tick && <i className={`rail-tick ${tick.cls}`}><Icon name={tick.glyph} size={6} /></i>}
    </span>
  );
}

const ACCEPT = (PROOF_MIME_TYPES as readonly string[]).join(',');

/** The greyed card: the band alone, Not applicable and why. `kind` is the card's own classes, e.g. `money ss-money`. */
export function SelfServiceCardNa({ kind, subject, title, why }: { kind: string; subject: CardSubject; title: string; why: string }) {
  return (
    <div className={`scard ${kind} is-na`}>
      <div className="ss-band na" aria-disabled="true">
        <SubjectMark subject={subject} />
        <span className="bt"><span className="ss-title">{title}</span><span className="bsent">Not applicable · {why}</span></span>
      </div>
    </div>
  );
}

export interface SelfServiceCardProps {
  /** The card's own classes, e.g. `money ss-money`; its tone class is added after `open`. */
  kind: string;
  subject: CardSubject;
  /** The card's ground, e.g. `blue` — drawn as `is-blue`. */
  tone: string;
  open: boolean;
  onToggle: () => void;
  title: string;
  band: Band;
  /** The words for the slot under the band; none on a closed stay. */
  message: { first: string; phone: string | null | undefined; text: string; prepare?: () => Promise<string | null> } | null;
  children: ReactNode;
  /** Drawn after the trail — the full-screen viewer. */
  after?: ReactNode;
}

export function SelfServiceCard({ kind, subject, tone, open, onToggle, title, band, message, children, after }: SelfServiceCardProps) {
  return (
    <div className={`scard ${kind}${open ? ' open' : ''} is-${tone}`} aria-live="polite">
      <button type="button" className={`ss-band ${band.tone}`} aria-expanded={open} onClick={onToggle}>
        <SubjectMark subject={subject} />
        <span className="bt"><span className="ss-title">{title}</span><span className="bsent">{band.sentence}</span></span>
        {/* The figure alone, top right (Freddi, 2026-10-11): the sentence under the title says the rest. */}
        <span className="bfig">{band.figure}</span>
        <Icon name="chevron-down" size={14} className="ss-chev" />
      </button>
      {/* THE MESSAGE SLOT (Freddi, 2026-10-11): under the band, shut or open — the card's words, ready to send. */}
      {message && <MessageSlot first={message.first} phone={message.phone} text={message.text} prepare={message.prepare} />}
      <DisclosureRegion open={open}>
        <div className="ss-trail">{children}</div>
      </DisclosureRegion>
      {after}
    </div>
  );
}

/** The row's thumbnail: the image, a PDF's mark, or an empty frame — a tap opens it full screen. */
export function TrailThumb({ proof, seen, emptyTitle, onFull }: {
  proof: { id: string; mime: string } | null;
  /** The card has been opened: only then are images fetched (PR #255 review, #42). */
  seen: boolean;
  emptyTitle: string;
  onFull: () => void;
}) {
  if (proof && isPdf(proof.mime)) {
    return <button type="button" className="th pdf" aria-label="Open the PDF full screen" onClick={onFull}><Icon name="file-text" size={14} /></button>;
  }
  if (proof) {
    return <button type="button" className="th has-img" aria-label="Open the screenshot full screen" onClick={onFull}>{seen && <img src={proofImageUrl(proof.id)} alt="" />}</button>;
  }
  return <span className="th empty" title={emptyTitle}><Icon name="image" size={12} /></span>;
}

export interface TrailRowProps {
  cls: TrailClass;
  /** Something came in for this row — a screenshot or a card — so its spine mark is green. */
  uploaded: boolean;
  /** The row's own state class when open — `ok`, `bad`, or none. */
  verdict?: 'ok' | 'bad' | null;
  thumb: ReactNode;
  title: string;
  sub: string;
  pill: Pill;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}

/** One row of the trail: the spine and its flag, the head that opens it, and what it holds once open. */
export function TrailRow({ cls, uploaded, verdict = null, thumb, title, sub, pill, open, onToggle, children }: TrailRowProps) {
  const openCls = open ? ' open vx-row' + (verdict ? ` ${verdict}` : '') : '';
  return (
    <div className={`ss-tr ${cls}`}>
      <span className="spine"><SpineMark cls={cls} uploaded={uploaded} /></span>
      <div>
        <div className={`ss-row${openCls}`}>
          <div className="hd">
            {thumb}
            <button type="button" className="t" aria-expanded={open} onClick={onToggle}>
              <b>{title}</b><small>{sub}</small>
            </button>
            <span className={`st ${pill.tone}${pill.glyph ? ' has-ic' : ''}`}>{pill.glyph && <Icon name={pill.glyph} size={10} />}{pill.word}</span>
            <Icon name="chevron-down" size={14} className="chev" />
          </div>
          <DisclosureRegion open={open}>
            <div className="vx">{children}</div>
          </DisclosureRegion>
        </div>
      </div>
    </div>
  );
}

/** The proof itself, large enough to read; a tap opens it full screen. `corner` sits over it (the Money card's ×). */
export function Shot({ id, mime, alt, muted = false, load = true, corner }: { id: string; mime: string; alt: string; muted?: boolean; load?: boolean; corner?: ReactNode }) {
  return (
    <span className="shot">
      {load ? <ProofImage id={id} alt={alt} muted={muted} mime={mime} /> : <span className="proof-img is-waiting" aria-hidden="true" />}
      {corner}
    </span>
  );
}

/** The decision under a proof waiting on Patricia: one field, its note, and the two answers. */
export function DecideBlock({ label, input, note, acceptable, busy, rejectLabel, onAccept, onReject, pending = null }: {
  label: string;
  input: ReactNode;
  note: string;
  acceptable: boolean;
  busy: boolean;
  rejectLabel: string;
  onAccept: () => void;
  onReject: () => void;
  /** The verdict in flight: its button carries the app's spinner and says so (Freddi, 2026-10-14). */
  pending?: 'accept' | 'reject' | null;
}) {
  return (
    <div className="vx-decide">
      <div className="row">
        <span className="k">{label}</span>
        {input}
      </div>
      <div className="n">{note}</div>
      <div className="vx-acts">
        <button type="button" className={'btn primary rq-yes' + (pending === 'accept' ? ' is-busy' : '')} disabled={busy || !acceptable} aria-busy={pending === 'accept' || undefined} onClick={onAccept}>
          <ButtonGlyph icon={<Icon name="check" size={12} />} busy={pending === 'accept'} />{pending === 'accept' ? 'Accepting…' : 'Accept'}
        </button>
        <button type="button" className={'btn quiet-danger rq-no' + (pending === 'reject' ? ' is-busy' : '')} disabled={busy} aria-busy={pending === 'reject' || undefined} onClick={onReject}>
          <ButtonGlyph icon={<Icon name="circle-x" size={12} />} busy={pending === 'reject'} />{pending === 'reject' ? 'Marking…' : rejectLabel}
        </button>
      </div>
    </div>
  );
}

/**
 * THE DECISION WHILE THE BOOKING RE-READS (Freddi, 2026-10-14: "it should change from accepting to
 * loading skeleton to finished state"): the decide block's own frame, its rows as skeleton bars, so the
 * row holds its height and the finished state arrives in place.
 */
export function DecideSkeleton() {
  const bar = (w: number | string, h: number, r?: string) => <span className="bl-block" style={{ display: 'inline-block', width: w, height: h, borderRadius: r }} />;
  return (
    <div className="vx-decide is-skeleton" aria-busy="true">
      <div className="row">{bar(70, 11)}{bar(96, 28, 'var(--radius-sm)')}</div>
      <div className="n">{bar('72%', 9)}</div>
      <div className="vx-acts">{bar(84, 30, 'var(--radius-ctl)')}{bar(96, 30, 'var(--radius-ctl)')}</div>
    </div>
  );
}

/** Patricia's own upload: the empty frame is the drop zone. `mc-drop` is the look both cards share; `className` adds a card's own. */
export function DropZone({ title, sub, busy, onFile, className = '' }: { title: string; sub: string; busy: boolean; onFile: (file: File) => void; className?: string }) {
  const pick = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (f) onFile(f);
  };
  return (
    <label className={'vx-shot empty drop mc-drop' + (className ? ` ${className}` : '') + (busy ? ' is-busy' : '')} aria-disabled={busy || undefined}>
      <Icon name="upload" size={22} />
      <b>{title}</b>
      <small>{sub}</small>
      <span className="btn">Choose file</span>
      <input type="file" accept={ACCEPT} disabled={busy} onChange={pick} hidden />
    </label>
  );
}
